import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {takeUntil} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {CommissionAnnex} from '../../../../shared/interface/commission-annex';
import {CommissionAnnexGroup} from '../../../../shared/interface/commission-annex-group';
import { v4 as uuidv4 } from 'uuid';
import {CommissionAnnexRisk} from '../../../../shared/interface/commission-annex-risk';
import {CommissionAnnexThreshold} from '../../../../shared/interface/commission-annex-threshold';
import {CommissionAnnexService} from '../../../../shared/services/commission-annex.service';
import _ from 'lodash';

@Component({
  selector: 'app-annex-group-form',
  templateUrl: './annex-group-form.component.html',
  styleUrls: ['./annex-group-form.component.scss']
})
export class AnnexGroupFormComponent implements OnInit, OnDestroy {
  @Input() annex: CommissionAnnex;
  @Output() closeModification: EventEmitter<boolean> = new EventEmitter<boolean>();

  formGroupsValid = false;
  formGroups: FormArray;
  processingRequest = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private commissionAnnexService: CommissionAnnexService,
    private snack: MatSnackBar,
  ) {
    this.formGroups = fb.array([]);
  }

  ngOnInit(): void {
    this.formGroups.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.formGroupsValid = this.formGroups.valid);

    this.annex.groups.forEach((commissionAnnexGroup: CommissionAnnexGroup) => {
      this.addGroup(commissionAnnexGroup);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  cancel(): void {
    this.closeModification.emit(false);
  }

  addGroup(initialValue?: CommissionAnnexGroup): void {
    const formGroup = this.fb.group({
      uuid: this.fb.control(initialValue ? initialValue.uuid : uuidv4()),
      risks: this.fb.array([], []),
      thresholds: this.fb.array([], []),
      ratePaid: this.fb.control(initialValue ? initialValue.ratePaid : ''),
      rates: this.fb.control(initialValue ? initialValue.ratesArray : [], []),
    });

    initialValue?.risks.forEach((commissionAnnexRisk: CommissionAnnexRisk) => {
      this.addRisk((formGroup.get('risks') as FormArray), commissionAnnexRisk);
    });

    initialValue?.thresholds.forEach((commissionAnnexThreshold: CommissionAnnexThreshold) => {
      this.addThreshold((formGroup.get('thresholds') as FormArray), commissionAnnexThreshold);
    });

    this.formGroups.push(formGroup);
  }

  removeGroup(i: number): void {
    this.formGroups.removeAt(i);
  }

  addRisk(formArray: FormArray, initialValue?: CommissionAnnexRisk): void {
    const riskFormGroup = this.fb.group({
      uuid: this.fb.control(initialValue ? initialValue.uuid : uuidv4()),
      groupName: this.fb.control(initialValue ? initialValue.groupName : ''),
      name: this.fb.control(initialValue ? initialValue.name : '', []),
      riders: this.fb.control(initialValue ? initialValue.riders : ''),
      ratePaid: this.fb.control(initialValue ? initialValue.ratePaid : ''),
      rates: this.fb.control(initialValue ? initialValue.ratesArray : [], []),
      riskDictionary: this.fb.control(initialValue ? initialValue.riskDictionary?.uuid : ''),
    });

    formArray.push(riskFormGroup);
  }

  removeRisk(formGroup: FormGroup, i: number): void {
    (formGroup.get('risks') as FormArray).removeAt(i);
  }

  addThreshold(formArray: FormArray, initialValue?: CommissionAnnexThreshold): void {
    const thresholdFormGroup = this.fb.group({
      uuid: this.fb.control(initialValue ? initialValue.uuid : uuidv4()),
      type: this.fb.control(initialValue ? initialValue.type?.id : ''),
      thresholdDictionary: this.fb.control(initialValue ? initialValue.thresholdDictionary?.uuid : ''),
      thresholdFrom: this.fb.control(initialValue ? initialValue.thresholdFrom : ''),
      thresholdTo: this.fb.control(initialValue ? initialValue.thresholdTo : ''),
    });

    formArray.push(thresholdFormGroup);
  }

  removeThreshold(formGroup: FormGroup, i: number): void {
    (formGroup.get('thresholds') as FormArray).removeAt(i);
  }

  save(): void {
    this.processingRequest = true;

    this.commissionAnnexService.configure(this.annex.uuid, {groups: this.formGroups.value})
      .subscribe({
        next: () => {
          this.processingRequest = false;
          this.snack.open('Konfiguracja została pomyślnie zapisana', '', {duration: 2000});
          this.closeModification.emit(true);
        },
        error: (error: HttpErrorResponse) => {
          this.processingRequest = false;
          this.snack.open('Przy zapisywaniu konfiguracji wystąpił błąd', error.status.toString(), {duration: 2000});
        }
      });
  }

  cloneGroup(i: number): void {
    const formGroup = _.cloneDeep(this.formGroups.controls[i]);
    formGroup.get('uuid').setValue(uuidv4());
    formGroup.get('risks').controls.forEach((riskFormGroup: FormGroup) => {
      riskFormGroup.get('uuid').setValue(uuidv4());
    });
    formGroup.get('thresholds').controls.forEach((thresholdFormGroup: FormGroup) => {
      thresholdFormGroup.get('uuid').setValue(uuidv4());
    });
    this.formGroups.insert(i, formGroup);
  }
}
